import { useEffect } from 'react';

import type { ClinicalTrial } from '../../_api/ClinicalTrialsApi';

import {
  createFilterConfig,
  filterItems,
  matchFilter,
  type UseFilterDrawerReturn,
  type UseFilterReturn,
  useFilter,
  useFilterDrawer,
} from '@helsenorge/designsystem-react/components/Filter';

import { sortAlphabetically, toUniqueValues } from '../../_helpers/string';
import { useSearchParams } from '../../_helpers/useSearchParams';

type ClinicalTrialsForm = {
  kategori: string[];
  status: string[];
  relatertBehandling: string[];
  deltakendeForetak: string[];
  fritekst: string;
};

export interface ClinicalTrialsResources {
  search?: string;
  category?: string;
  status?: string;
  relevantTreatment?: string;
  studyTakesPlaceAt?: string;
}

type FilterView = Exclude<keyof ClinicalTrialsForm, 'fritekst'>;
type FilterCategory = Exclude<keyof ClinicalTrialsForm, 'fritekst'>;

const filterCategories: FilterCategory[] = ['status', 'relatertBehandling', 'kategori', 'deltakendeForetak'];

interface ClinicalTrialsFilter {
  filter: UseFilterReturn<ClinicalTrialsForm>;
  filterCategories: { key: FilterCategory; options: string[] }[];
  filterLabels: Record<keyof ClinicalTrialsForm, string>;
  filteredClinicalTrials: ClinicalTrial[];
  drawer: UseFilterDrawerReturn<FilterView>;
  getLabel: (key: keyof ClinicalTrialsForm, value: unknown) => string;
  updateSearchQuery: (value: string) => void;
  toggleFilter: (key: FilterCategory, value: string) => void;
  removeFilterValue: (key: keyof ClinicalTrialsForm, value: unknown) => void;
  resetFilters: () => void;
  resetFilterCategory: (key: FilterCategory) => void;
}

export const useClinicalTrialsFilter = (
  clinicalTrials: ClinicalTrial[],
  conceptId: string | undefined,
  resources?: ClinicalTrialsResources
): ClinicalTrialsFilter => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryOptions = clinicalTrials
    .map(({ kategorier }) => kategorier ?? [])
    .flat()
    .reduce(toUniqueValues, [] as string[])
    .sort(sortAlphabetically);
  const statusOptions = clinicalTrials
    .map(({ status }) => status)
    .reduce(toUniqueValues, [] as string[])
    .sort(sortAlphabetically);
  const relatedTreatmentOptions = clinicalTrials
    .map(({ relaterte_behandlinger: behandlinger }) => behandlinger ?? [])
    .flat()
    .reduce(toUniqueValues, [] as string[])
    .sort(sortAlphabetically);
  const participatingOrganizationOptions = clinicalTrials
    .map(({ deltakende_foretak: foretak }) => foretak ?? [])
    .flat()
    .reduce(toUniqueValues, [] as string[])
    .sort(sortAlphabetically);
  const getSearchParamValues = (key: FilterCategory): string[] => searchParams?.getAll(key).filter(Boolean) ?? [];
  const { filterOptions, getLabel } = createFilterConfig<ClinicalTrialsForm>({
    kategori: {
      options: categoryOptions.map(value => ({ value, label: value })),
      defaultValue: getSearchParamValues('kategori'),
      getLabel: option => option.label,
    },
    status: {
      options: statusOptions.map(value => ({ value, label: value })),
      defaultValue: getSearchParamValues('status'),
      getLabel: option => option.label,
    },
    relatertBehandling: {
      options: relatedTreatmentOptions.map(value => ({ value, label: value })),
      defaultValue: getSearchParamValues('relatertBehandling'),
      getLabel: option => option.label,
    },
    deltakendeForetak: {
      options: participatingOrganizationOptions.map(value => ({ value, label: value })),
      defaultValue: getSearchParamValues('deltakendeForetak'),
      getLabel: option => option.label,
    },
    fritekst: {
      defaultValue: searchParams?.get('query') ?? '',
      getLabel: value => String(value),
    },
  });
  const filter = useFilter<ClinicalTrialsForm>(filterOptions);
  const drawer = useFilterDrawer<FilterView>();

  const updateSearchParamValues = (key: FilterCategory, values: string[]): void => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(key);
    values.forEach(value => nextParams.append(key, value));
    setSearchParams(nextParams, { replace: true });
  };

  const updateSearchQuery = (value: string): void => {
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set('query', value);
    } else {
      nextParams.delete('query');
    }
    setSearchParams(nextParams, { replace: true });
    filter.setFilter('fritekst', value || undefined);
  };

  const toggleFilter = (key: FilterCategory, value: string): void => {
    const currentValues = filter.filters[key] ?? [];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter(currentValue => currentValue !== value)
      : [...currentValues, value];
    filter.setFilter(key, nextValues.length ? nextValues : undefined);
    updateSearchParamValues(key, nextValues);
  };

  const removeFilterValue = (key: keyof ClinicalTrialsForm, value: unknown): void => {
    if (key === 'fritekst') {
      updateSearchQuery('');
      return;
    }

    const nextValues = (filter.filters[key] ?? []).filter(currentValue => currentValue !== value);
    filter.setFilter(key, nextValues.length ? nextValues : undefined);
    updateSearchParamValues(key, nextValues);
  };

  const resetFilters = (): void => {
    filter.resetFiltersToEmpty();
    const nextParams = new URLSearchParams(searchParams);
    filterCategories.forEach(key => nextParams.delete(key));
    setSearchParams(nextParams, { replace: true });
  };

  const resetFilterCategory = (key: FilterCategory): void => {
    filter.setFilter(key, undefined);
    updateSearchParamValues(key, []);
  };

  useEffect(() => {
    const kategori = getSearchParamValues('kategori');
    const status = getSearchParamValues('status');
    const relatertBehandling = getSearchParamValues('relatertBehandling');
    const deltakendeForetak = getSearchParamValues('deltakendeForetak');

    filter.setFilters({
      kategori: kategori.length ? kategori : undefined,
      status: status.length ? status : undefined,
      relatertBehandling: relatertBehandling.length ? relatertBehandling : undefined,
      deltakendeForetak: deltakendeForetak.length ? deltakendeForetak : undefined,
      fritekst: searchParams?.get('query') || undefined,
    });
  }, [searchParams]);

  const filterLabels: Record<keyof ClinicalTrialsForm, string> = {
    kategori: resources?.category ?? 'Health category',
    status: resources?.status ?? 'Study status',
    relatertBehandling: resources?.relevantTreatment ?? 'Diagnosis',
    deltakendeForetak: resources?.studyTakesPlaceAt ?? 'Location',
    fritekst: resources?.search ?? 'Search',
  };
  const clinicalTrialsForDiagnosis = clinicalTrials.filter(
    trial => !conceptId || trial.relaterte_diagnoser?.some(({ diagnose_kode }) => diagnose_kode === conceptId)
  );
  const filteredClinicalTrials = filterItems(clinicalTrialsForDiagnosis, filter.filters, {
    kategori: matchFilter.arrayIncludes<ClinicalTrial>(trial => trial.kategorier ?? []),
    status: matchFilter.exactMatch<ClinicalTrial>(trial => trial.status),
    relatertBehandling: matchFilter.arrayIncludes<ClinicalTrial>(trial => trial.relaterte_behandlinger ?? []),
    deltakendeForetak: matchFilter.arrayIncludes<ClinicalTrial>(trial => trial.deltakende_foretak ?? []),
    fritekst: matchFilter.textSearch<ClinicalTrial>(
      trial => trial.tittel,
      trial => trial.sammendrag ?? undefined
    ),
  });

  return {
    filter,
    filterCategories: [
      { key: 'status' as const, options: statusOptions },
      { key: 'relatertBehandling' as const, options: relatedTreatmentOptions },
      { key: 'kategori' as const, options: categoryOptions },
      { key: 'deltakendeForetak' as const, options: participatingOrganizationOptions },
    ],
    filterLabels,
    filteredClinicalTrials,
    drawer,
    getLabel,
    updateSearchQuery,
    toggleFilter,
    removeFilterValue,
    resetFilters,
    resetFilterCategory,
  };
};
